import { Button, Stack, Box, Slide } from '@mui/material';
import { VyIcon } from '@weft/icons';
import { withGlobalSearchParams } from 'apps/app/src/components/utility/GlobalSearchParamsBoundary';
import type { ReportSummary } from 'apps/app/src/hooks/requests/use-report-summaries';
import { useCarouselNavigation } from 'apps/app/src/hooks/use-carousel-navigation';
import { useTabSelection } from 'apps/app/src/hooks/use-tab-selection';
import React from 'react';
import { ReportTab } from './ReportTab';

type TabCarouselProps = {
  reportSummaries: ReportSummary[];
};

const ReportTabsCarousel: React.FC<TabCarouselProps> = ({ reportSummaries }) => {
  const { currentPageIndex, slideDirection, handlePageNavigation } = useCarouselNavigation(0);
  const { activeTabType, handleTabSelection } = useTabSelection();
  
  const tabsPerPage = 6;
  const totalPageCount = Math.max(Math.ceil(reportSummaries.length / tabsPerPage), 1);
  const tabsHiddenLeftCount = currentPageIndex * tabsPerPage;
  const tabsHiddenRightCount = reportSummaries.length - (currentPageIndex + 1) * tabsPerPage;
  const shouldShowPreviousButton = currentPageIndex > 0;
  const shouldShowNextButton = currentPageIndex < totalPageCount - 1;

  const getTabsForPage = (pageIndex: number) => {
    const startIndex = pageIndex * tabsPerPage;
    const endIndex = startIndex + tabsPerPage;

    return reportSummaries.slice(startIndex, endIndex);
  };

  return (
    <Box
      sx={{
        display: '-webkit-box',
        alignItems: 'center',
        width: '100%',
        position: 'relative',
        height: '72px',
      }}
    >
      {shouldShowPreviousButton && (
        <Button
          variant="contained"
          color="inherit"
          size="medium"
          onClick={() => handlePageNavigation('prev')}
          disabled={!shouldShowPreviousButton}
        >
          <VyIcon icon="ArrowSmallLeft" />
          {`more ${tabsHiddenLeftCount}`}
        </Button>
      )}

      {Array.from({ length: totalPageCount }).map((_, pageIndex) => (
        <Box
          key={pageIndex}
          sx={{
            width: '100%',
            display: currentPageIndex === pageIndex ? 'block' : 'none',
          }}
        >
          <Slide
            direction={slideDirection}
            in={currentPageIndex === pageIndex}
          >
            <Stack
              spacing={2}
              direction="row"
            >
              {getTabsForPage(pageIndex).map((tab, index) => (
                <ReportTab
                  key={index}
                  title={tab.title}
                  count={tab.count}
                  isActive={tab.title === activeTabType}
                  onClick={handleTabSelection}
                  sx={{
                    '&:hover': {
                      backgroundColor: shouldShowNextButton && index === getTabsForPage(pageIndex).length - 1 ? '#fff' : '',
                    },
                  }}
                />
              ))}
            </Stack>
          </Slide>
        </Box>
      ))}
      {shouldShowNextButton && (
        <Box
          sx={{
            background: 'linear-gradient(270deg, #F5F5F5 42.45%, rgba(245, 245, 245, 0) 100%)',
            backgroundColor: 'white',
            width: '110px',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
            position: 'absolute',
            right: 0,
            backdropFilter: 'blur(10px)',
          }}
        >
          <Button
            variant="contained"
            color="inherit"
            size="medium"
            onClick={() => handlePageNavigation('next')}
            disabled={!shouldShowNextButton}
          >
            {`more ${tabsHiddenRightCount}`}
            <VyIcon icon="ArrowSmallRight" />
          </Button>
        </Box>
      )}
    </Box>
  );
};

const ReportTabsCarouselWithGlobalSearch = withGlobalSearchParams(ReportTabsCarousel);
export { ReportTabsCarouselWithGlobalSearch as ReportTabsCarousel };
